import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";

/** The signed-in partner, as /api/me gives it. */
export interface Me {
  partner: { id: string; displayName: string; code?: string; kind: string; language: "en" | "fr"; payoutCurrency: string; country: string; tier: string; onboarding?: Record<string, string>; certifiedAt?: string; city?: string };
  expert: boolean;
  tier?: { key: string; cap: number; salesUsdMinor: number; next?: { key: string; cap: number; min: number; remainingMinor: number; progressPct: number }; tiers: { key: string; cap: number; min: number }[] };
  onboarding?: { done: string[]; pct: number; current?: string };
  certified: boolean;
  profile: { photoUrl?: string; bio?: string };
  session: { email: string; name: string; operator: boolean };
  accountManager: { name: string; email: string; phone: string; hours: string };
  balance?: { balanceMinor: number; pendingReleaseMinor: number; currency: string };
}

export interface Shell {
  me: Me;
  reload: () => Promise<void>;
  go: (view: string) => void;
  toast: (msg: string) => void;
  openLounge: (onDone?: () => void) => void;
  ask: (text?: string, quoteId?: string) => void;
  clientView: boolean;
}
export const ShellContext = createContext<Shell | null>(null);
export const useShell = () => useContext(ShellContext)!;

/** Loads a BFF resource; reloads when `deps` change. */
export function useApi<T>(path: string | null, deps: unknown[] = []): { data: T | undefined; error: string | undefined; reload: () => void } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [tick, setTick] = useState(0);
  const { clientView } = useContext(ShellContext) ?? { clientView: false };
  useEffect(() => {
    if (!path) return;
    let live = true;
    setError(undefined);
    api<T>(path).then((d) => live && setData(d), (e: Error) => live && setError(e.message));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, clientView, ...deps]);
  return { data, error, reload: useCallback(() => setTick((t) => t + 1), []) };
}

export function Head({ eyebrow, title, extra }: { eyebrow: string; title: string; extra?: ReactNode }) {
  return (
    <div className="pk-head">
      <div><span className="eyebrow brandc">{eyebrow}</span><h2 className="serif" style={{ fontSize: "clamp(1.8rem,3.4vw,2.5rem)" }}>{title}</h2></div>
      {extra ? <div className="pk-tools">{extra}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="dlg-top"><div className="row"><b>{title}</b><button className="x" type="button" onClick={onClose} aria-label="Close">×</button></div></div>
        <div className="m-body">{children}</div>
      </div>
    </div>
  );
}

/** A scene as the plate of a card (the prototype's SVG illustrations). */
export function Plate({ svg, children }: { svg: string; children?: ReactNode }) {
  return <div className="plate"><span style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: svg }} />{children}</div>;
}

export const Loading = () => <p className="muted">…</p>;
export const Failed = ({ error }: { error: string }) => <div className="card empty"><p className="muted">{error}</p></div>;

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

export const I = {
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 11l8-7 8 7v9h-5v-6H9v6H4z" /></svg>,
  dash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 13l3-8h12l3 8v6H3z" /><path d="M3 13h5l1 3h6l1-3h5" /></svg>,
  ann: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 10v4h3l6 4V6L7 10z" /><path d="M17 9a4 4 0 010 6" /></svg>,
  perf: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>,
  earn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M15 9.5c-.5-1-1.6-1.5-3-1.5-1.7 0-3 .8-3 2s1.3 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.4 0-2.5-.5-3-1.5M12 6v12" /></svg>,
  badge: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="9" r="6" /><path d="M8.5 14L7 22l5-3 5 3-1.5-8" /></svg>,
  pk: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="7" width="16" height="13" rx="2" /><path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="4" width="16" height="17" rx="2" /><path d="M8 2v4M16 2v4M4 10h16M8 14h5M8 17h8" /></svg>,
  trips: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 6h16v13H4z" /><path d="M9 6V4h6v2M4 11h16" /></svg>,
  plane: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 16l20-6-4-3-6 2-6-4-2 1 4 5-4 1-2-1-1 1z" /><path d="M3 21h18" /></svg>,
  people: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 4.5a3.5 3.5 0 010 7M21.5 20c0-2.8-1.6-4.8-4-5.6" /></svg>,
  gift: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 10h16v10H4zM2 7h20v3H2zM12 7v13" /></svg>,
  refer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="8" r="3.5" /><path d="M2 20c0-3.6 3-6 7-6s7 2.4 7 6M19 8v6M16 11h6" /></svg>,
  forum: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 5h16v11H8l-4 4z" /></svg>,
  events: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  academy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 9l10-5 10 5-10 5z" /><path d="M6 11v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" /></svg>,
  compass: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></svg>,
  help: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 015 0c0 1.8-2.5 2-2.5 4M12 17h.01" /></svg>,
  plus: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>,
  search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>,
  menu: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h16" /></svg>,
  spark: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8zM19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9z" /></svg>,
};
